import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import {
  EMITTER_ERROR,
  EMITTER_INFO,
  EMITTER_SUCCESS,
  EMITTER_WARNING,
  SNACKBAR_CONFIG as config,
  tenants_api_endpoints,
} from "../utils/constants";
import CustomEventEmitter from "../utils/emitter/EventEmitter";
import DashboardLayout from "../components/Sidebar/DashboardLayout";
import {
  HOME_PATH,
  SIGNUP_PATH,
  PAYMENT_SUCCESS_PATH,
  PAYMENT_CANCEL_PATH,
  ONBOARDING_PAYMENT_SUCCESS_PATH,
  ONBOARDING_PAYMENT_CANCEL_PATH,
  DASHBOARD_PATH,
  EMAIL_MARKETING_CAMPAIGN_PATH,
  ACCESS_DENIED_PATH,
} from "../utils/constants/routes";
import { Loading, InvalidRouteScreen } from "../screens";
import { authRoutes } from "./authRoutes";
import { userRoutes } from "./userRoutes";
import { SNACKBAR_CLOSE_ICON } from "../utils/constants/assets";
import { SnackbarCloseIcon } from "../components/Snackbar";
import { useDispatch, useSelector } from "react-redux";
import { checkLogin, verifyDomain } from "../redux/actions/authActions";
import { initialRoutes } from "./initialRoutes";
import { HOST_URL, protocol } from "../utils/constants/httpConstants";
import { FullScreenLoader } from "../components";
import { defaultRoutes } from "./defaultRoutes";

const Router = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const userDetailsSelector = useSelector((state) => state?.userDetails);
  const userDetails = userDetailsSelector?.payload;
  const navigate = useNavigate();
  const location = useLocation();
  const [checkLoginDone, setCheckLoginDone] = useState(false);
  const [checkDomainValidation, setCheckDomainValidation] = useState(true);
  const [checkValidateSubdomainDone, setCheckValidateSubdomainDone] =
    useState(false);
  const [isRefreshTokenCall, setIsrefreshTokenCall] = useState(false);
  const [checkToken, setCheckToken] = useState(false);

  useEffect(() => {
    // validate subdomain here.
    if (
      !checkValidateSubdomainDone
      // && !window.location.href.includes("auth/register")
    ) {
      setCheckToken(true);

      // Allow overriding tenant for local testing via query parameter (e.g. ?tenant=aktohcyber)
      const urlParams = new URLSearchParams(window.location.search);
      const tenantParam = urlParams.get("tenant");

      if (tenantParam) {
        localStorage.setItem("tenant_domain", tenantParam);
      }

      const storedTenant = localStorage.getItem("tenant_domain");

      const host = storedTenant
        ? `${storedTenant}.reachiq.ai`
        : window.location.host;

      let hostLength = host?.trim()?.split(".");
      if (hostLength.length > 1 || storedTenant) {
        const subDomain = storedTenant
          ? `${storedTenant}.reachiq.ai`
          : host?.trim()?.split(".")[0] + ".reachiq.ai";

        (async () => {
          try {
            await dispatch(verifyDomain(subDomain, navigate));
            setIsrefreshTokenCall(true);
          } catch (error) {
            setIsrefreshTokenCall(false);
            setCheckToken(false);
            setCheckDomainValidation(false);
            console.log("error---->", error);
          }
        })();
        setCheckValidateSubdomainDone(true);
      } else {
        window.location.href = `${protocol}${HOST_URL}${SIGNUP_PATH}`;
      }
    } else {
      setCheckDomainValidation(false);
    }

    if (window.location.pathname === "/") {
      setCheckToken(false);
    }

    // refresh token api call here.
    if (
      isRefreshTokenCall &&
      !checkLoginDone &&
      !userDetails &&
      !window.location.href.includes("auth/meetings") &&
      !(window.location.pathname === "/") &&
      !window.location.href.includes("auth/register") &&
      !window.location.href.includes("auth/google-signup") &&
      !window.location.href.includes("auth/reset_password") &&
      !window.location.href.includes("auth/unsubscribe-successful") &&
      !window.location.href.includes("auth/forgot_password") &&
      !window.location.href.includes("/cp") &&
      !window.location.href.includes("/cancel-meeting")
    ) {
      (async () => {
        await dispatch(checkLogin(navigate, tenants_api_endpoints));
        setCheckToken(false);
      })();
      setCheckLoginDone(true);
      // check conflict 2.
    } else {
      setCheckToken(false);
    }

    CustomEventEmitter.on(EMITTER_SUCCESS, (message) => {
      enqueueSnackbar(message, {
        variant: "success",
        preventDuplicate: true,
        anchorOrigin: config.snackBarPlacement,
        action: (key) => (
          <SnackbarCloseIcon
            src={SNACKBAR_CLOSE_ICON}
            onClick={() => {
              closeSnackbar(key);
            }}
          />
        ),
      });
    });
    CustomEventEmitter.on(EMITTER_ERROR, (message) => {
      enqueueSnackbar(message, {
        variant: "error",
        preventDuplicate: true,
        anchorOrigin: config.snackBarPlacement,
        action: (key) => (
          <SnackbarCloseIcon
            src={SNACKBAR_CLOSE_ICON}
            onClick={() => {
              closeSnackbar(key);
            }}
          />
        ),
      });
    });
    CustomEventEmitter.on(EMITTER_INFO, (message) => {
      enqueueSnackbar(message, {
        variant: "info",
        preventDuplicate: true,
        anchorOrigin: config.snackBarPlacement,
      });
    });
    CustomEventEmitter.on(EMITTER_WARNING, (message) => {
      enqueueSnackbar(message, {
        variant: "warning",
        preventDuplicate: true,
        anchorOrigin: config.snackBarPlacement,
        action: (key) => (
          <SnackbarCloseIcon
            src={SNACKBAR_CLOSE_ICON}
            onClick={() => {
              closeSnackbar(key);
            }}
          />
        ),
      });
    });
    // Role based route protection.
    // Clicking a forbidden tab SHOWS the access-denied screen — that is the
    // point of the screen, so do not silently bounce to the dashboard here.
    // ACCESS_DENIED_PATH stays in the allow-list so the screen can render
    // instead of being redirected away from.
    //
    // The "logged in and landed on access-denied" bug (blazent, 2026-08-14) is
    // NOT from this guard: checkLogin (authActions) restores the previous URL on
    // load, so once an SDR had been bounced here, every later login replayed
    // /user/access-denied. That is fixed on the checkLogin side, where
    // ACCESS_DENIED_PATH is deliberately NOT an allowed landing path — keep the
    // two lists asymmetric on purpose.
    if (userDetails && userDetails.role_access === "admin-sdr") {
      const allowedPaths = [
        DASHBOARD_PATH,
        EMAIL_MARKETING_CAMPAIGN_PATH,
        ACCESS_DENIED_PATH,
        "/", // Initial redirect handling
      ];

      const currentPath = location.pathname;

      // Never restrict auth routes
      if (currentPath.startsWith("/auth")) {
        return;
      }

      const isAllowed = allowedPaths.some((path) => {
        if (path === "/") return currentPath === "/";
        return currentPath === path || currentPath.startsWith(path + "/");
      });

      // Stripe return pages are exempt. This used to be
      // `!currentPath.includes("payment")` — a substring match anywhere in the
      // path, so ANY url with "payment" in it slipped past the guard. Pinned to
      // the four real payment routes instead.
      const isPaymentReturn = [
        PAYMENT_SUCCESS_PATH,
        PAYMENT_CANCEL_PATH,
        ONBOARDING_PAYMENT_SUCCESS_PATH,
        ONBOARDING_PAYMENT_CANCEL_PATH,
      ].includes(currentPath);
      if (!isAllowed && !isPaymentReturn) {
        navigate(ACCESS_DENIED_PATH);
      }
    }

    //for alert messages - ends
    return () => {
      CustomEventEmitter.removeEventListener("alert_success");
      CustomEventEmitter.removeEventListener("alert_error");
      CustomEventEmitter.removeEventListener("alert_info");
      CustomEventEmitter.removeEventListener("alert_warning");
    };
  }, [
    enqueueSnackbar,
    closeSnackbar,
    isRefreshTokenCall,
    userDetails,
    location.pathname,
  ]);

  return checkToken || checkDomainValidation ? (
    <FullScreenLoader />
  ) : (
    <Routes>
      {/* Base Route, starting of the application */}
      <Route path={HOME_PATH} element={<Loading />} />

      {/* All Auth routes  */}
      {authRoutes.map(({ path, component: Component }, index) => (
        <Route path={path} element={<Component />} key={index} exact />
      ))}

      {/* All Authorized, user routes */}
      {userRoutes.map(({ path, component: Component }, index) => {
        const isPaymentRoute =
          path === PAYMENT_SUCCESS_PATH ||
          path === PAYMENT_CANCEL_PATH ||
          path === ONBOARDING_PAYMENT_SUCCESS_PATH ||
          path === ONBOARDING_PAYMENT_CANCEL_PATH;
        return (
          <Route
            path={path}
            element={
              isPaymentRoute ? (
                <Component />
              ) : (
                <DashboardLayout>
                  <Component />
                </DashboardLayout>
              )
            }
            key={index}
            exact
          />
        );
      })}

      {/* Initial Routes */}
      {initialRoutes.map(({ path, component: Component }, index) => {
        return <Route path={path} element={<Component />} key={index} exact />;
      })}

      {/* Default Routes */}
      {defaultRoutes.map(({ path, component: Component }, index) => {
        return <Route path={path} element={<Component />} key={index} exact />;
      })}

      {/* default error route */}
      <Route path="*" element={<InvalidRouteScreen />} />
    </Routes>
  );
};

export default Router;
